import CloseIcon from '@mui/icons-material/Close';
import { Button, Grow, Typography } from '@mui/material';
import { styled } from '@mui/styles';
import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux-latest';
import { ActionState } from '../../../../../../../enums/actions';
import { UploadCompaniesBreadCrumbs } from '../../../../../../../enums/breadcrumbs';
import { UploadCompaniesContext } from '../../../../../../../providers/upload-companies-provider';
import { thunkUpdateSearch } from '../../../../../../../redux/ducks/prospect-search-filters/operations';
import { AppDispatch } from '../../../../../../../redux/store/configureStore';
import UploadCompaniesModalStyled from './search-companies-modal.styled';
import { MatchCompanies } from './steps/match-companies';
import UploadList from './steps/modal-upload-list';

const UploadCompaniesStepTitles = {
    [UploadCompaniesBreadCrumbs.UPLOAD_LIST]: 'Upload list with Companies',
    [UploadCompaniesBreadCrumbs.MATCH_COMPANIES]: 'Match companies',
    [UploadCompaniesBreadCrumbs.SUBMIT]: 'Submit',
};

type Props = {
    closeModal: () => void;
    isOpen: boolean;
    exclude: boolean;
    includeValues: [];
    excludeValues: [];
};

const ProgressBar = styled('div')(({ progress, theme: { palette } }: any) => ({
    display: 'flex',
    width: `${progress * 100}%`,
    flexDirection: 'column',
    justifyContent: 'center',
    color: palette.colors.white,
    textAlign: 'center',
    backgroundColor: palette.primary.main,
    transition: '1s width',
}));

export const UploadCompaniesModal = ({ closeModal, isOpen, exclude, includeValues, excludeValues }: Props) => {
    const { currentView, actionState, handleViewChange, selectedCompanies, handleLoadingChange } =
        useContext(UploadCompaniesContext);
    const dispatch = useDispatch<AppDispatch>();
    const [step, setStep] = useState({
        [UploadCompaniesBreadCrumbs.UPLOAD_LIST]: { completed: true },
        [UploadCompaniesBreadCrumbs.MATCH_COMPANIES]: { completed: false },
        [UploadCompaniesBreadCrumbs.SUBMIT]: { completed: false },
    });
    const [stepTitle, setStepTitle] = useState(UploadCompaniesStepTitles[UploadCompaniesBreadCrumbs.UPLOAD_LIST]);
    const progressValue = Object.keys(step).filter((el) => step[el].completed).length / 3;

    useEffect(() => {
        if (currentView === UploadCompaniesBreadCrumbs.UPLOAD_LIST) {
            setStepTitle(UploadCompaniesStepTitles[UploadCompaniesBreadCrumbs.UPLOAD_LIST]);
        }
        if (currentView === UploadCompaniesBreadCrumbs.MATCH_COMPANIES) {
            setStepTitle(UploadCompaniesStepTitles[UploadCompaniesBreadCrumbs.MATCH_COMPANIES]);
        }
    }, [currentView]);

    const setContentTemplate = () => {
        switch (currentView) {
            case UploadCompaniesBreadCrumbs.UPLOAD_LIST:
                return <UploadList />;
            case UploadCompaniesBreadCrumbs.MATCH_COMPANIES:
                return <MatchCompanies />;
            default:
                return <></>;
        }
    };

    const handleSetCurrentView = () => {
        handleViewChange(UploadCompaniesBreadCrumbs.MATCH_COMPANIES);
        setStep({ ...step, [UploadCompaniesBreadCrumbs.MATCH_COMPANIES]: { completed: true } });
        handleLoadingChange(true);
    };

    const handleActionButtonClick = () => {
        if (currentView === UploadCompaniesBreadCrumbs.UPLOAD_LIST) {
            handleSetCurrentView();
        }
        if (currentView === UploadCompaniesBreadCrumbs.MATCH_COMPANIES) {
            handleSubmitCompanies();
        }
    };

    const handleSubmitCompanies = () => {
        const formattedCompanies = selectedCompanies
            .filter((c) => c.isValid)
            .map((c) => ({ id: c.id, name: c.name, website: c.website }));

        if (exclude) {
            formattedCompanies.push(...excludeValues);
        } else {
            formattedCompanies.push(...includeValues);
        }

        dispatch(
            thunkUpdateSearch({
                value: formattedCompanies,
                point: exclude ? 'excludeOrganizations' : 'organizations',
            })
        );
        closeModal();
    };

    return (
        <UploadCompaniesModalStyled>
            <Grow in={isOpen}>
                <div className="paper">
                    <div className="header">
                        <Typography className="title">{stepTitle}</Typography>
                        <div className="close-icon" onClick={closeModal}>
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="content">{setContentTemplate()}</div>
                    <div className="footer">
                        <div className="bread-crumbs">
                            {Object.keys(step).map((breadCrumb) => (
                                <div key={breadCrumb} className={`single ${step[breadCrumb].completed && 'completed'}`}>
                                    {`${breadCrumb}${breadCrumb !== UploadCompaniesBreadCrumbs.SUBMIT ? ' /' : ''}`}
                                </div>
                            ))}
                        </div>
                        {selectedCompanies.length > 0 && (
                            <div className="match-counter">
                                <div className="counter">
                                    <b>
                                        {selectedCompanies.filter((c) => c.isValid).length} / {selectedCompanies.length}
                                    </b>{' '}
                                    companies matched
                                </div>
                                <div className="info">Unmatched companies will be skipped</div>
                            </div>
                        )}
                        <div className="action">
                            <Button
                                data-Cy="continue-button"
                                onClick={handleActionButtonClick}
                                variant="contained"
                                disableElevation
                                disabled={actionState === ActionState.DISABLED}
                                className={`action-button ${actionState === ActionState.DISABLED && 'disabled'}`}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>
                    <div className="progress">
                        <ProgressBar progress={progressValue} />
                    </div>
                </div>
            </Grow>
        </UploadCompaniesModalStyled>
    );
};
